import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import * as dashboardService from "../services/dashboard.services";
import * as dashboardGraphService from "../services/dashboard.graph.service";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { Placeholder, SelectPicker } from "rsuite";
import { getBarChartDefaultOptions, getUser, handleServerValidations, setLocalKey } from "../../../utils/commonfunction";
import { chartFilterOptions, ROLE } from "../../../utils/Constants";
import axiosInstance from "../../../utils/axios";
import Swal from "sweetalert2";
import * as paymentsService from "../services/payments.service";

const Dashboard = () => {
  const { t } = useTranslation();
  const [showDashboardCount, setShowDashboardCount] = useState({});
  const [profile, setProfile] = useState(null);

  const getCurrentUser = () => {
    for (const key of Object.values(ROLE)) {
      const u = getUser(key);
      if (u && u.id) {
        return { ...u, activeRole: key };
      }
    }
    return null;
  };

  const formatDateLabel = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  };

  const limitCategories = (dates) => {
    const total = dates.length;
    if (total <= 10) return dates;
    const step = Math.ceil(total / 10);
    return dates.map((date, i) => (i % step === 0 ? date : ""));
  };

  /** User Graph */
  const [userGraphTimeLimit, setUserGraphTimeLimit] = useState("7d");
  const [userGraphLoader, setUserGraphLoader] = useState(false);
  const [userGraphData, setUserGraphData] = useState({
    series: [
      { name: "Club Admins", data: [] },
      { name: "Basic Users", data: [] },
      { name: "Parents", data: [] }, 
      { name: "Players", data: [] },
    ],
    options: {
      chart: {
        type: "area",
        toolbar: { show: false },
        zoom: { enabled: false },
        background: "#fff",
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.25,
          opacityTo: 0.02,
          stops: [0, 90, 100],
        },
      },
      markers: {
        size: 5,
        strokeWidth: 2,
        fillOpacity: 1,
        hover: { size: 7 },
      },
      grid: {
        borderColor: "#e0e0f0",
        strokeDashArray: 4,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: true } },
      },
      colors: ["#a78bfa", "#34d399", "#f59e0b", "#f87171"],
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "center",
        markers: { radius: 12 },
        fontSize: "13px",
      },
      yaxis: {
        labels: {
          formatter: (val) => val.toFixed(0),
        },
      },
      xaxis: {
        categories: [],
        labels: {
          style: { fontSize: "12px", colors: "#888" },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        title: {
          text: "Date",
          style: { fontSize: "12px", color: "#888" },
        },
      },
      tooltip: {
        y: { formatter: (val) => val.toFixed(0) },
        marker: {
          fillColors: ["#a78bfa", "#34d399", "#f59e0b", "#f87171"],
        },
      },
      dataLabels: { enabled: false },
    },
  });

  useEffect(() => {
    setUserGraphLoader(true);
    dashboardGraphService
      .userGraphApi(userGraphTimeLimit)
      .then((res) => {      
        const responseData = res?.data;
        const list = Array.isArray(responseData) ? responseData : responseData?.data || [];
        if (list.length > 0) {
          const seriesKeys = ["clubAdmin", "nonAffiliatedUser", "parent", "player"];
          const seriesLabels = {
            clubAdmin: "Club Admins",
            nonAffiliatedUser: "Basic Users",
            parent: "Parents",
            player: "Players",
          };
          const series = seriesKeys.map((key) => ({
            name: seriesLabels[key],
            data: list.map((item) => item[key] || 0),
          }));

          const datesFull = list.map((item) => new Date(item.date));

          setUserGraphData((prev) => ({
            ...prev,
            series,
            options: {
              ...prev.options,
              xaxis: {
                ...prev.options.xaxis,
                categories: limitCategories(list.map((item) => formatDateLabel(item.date))),
              },
              tooltip: {
                x: {
                  formatter: function (value, { dataPointIndex }) {
                    const fullDate = datesFull[dataPointIndex];
                    return fullDate ? fullDate.toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    }) : "";
                  },
                },
                y: { formatter: (val) => val.toFixed(0) },
                marker: {
                  fillColors: ["#a78bfa", "#34d399", "#f59e0b", "#f87171"],
                },
              },
            },
          }));
        } else {
          setUserGraphData((prev) => ({
            ...prev,
            series: [
              { name: "Club Admins", data: [] },
              { name: "Basic Users", data: [] },
              { name: "Parents", data: [] },
              { name: "Players", data: [] },
            ],
            options: {
              ...prev.options,
              xaxis: {
                ...prev.options.xaxis,
                categories: [],
              },
            },
          }));
        }
      })
      .catch((error) => console.log("userGraphApi error ====> ", error))
      .finally(() => setUserGraphLoader(false));
  }, [userGraphTimeLimit]);

  /** Match Graph */
  const [matchGraphTimeLimit, setMatchGraphTimeLimit] = useState("7d");
  const [matchGraphLoader, setMatchGraphLoader] = useState(false);
  const [matchGraphData, setMatchGraphData] = useState({
    series: [{ name: "Total Matches", data: [] }],
    options: {
      chart: {
        type: "area",
        toolbar: { show: false },
        zoom: { enabled: false },
        background: "#fff",
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.25,
          opacityTo: 0.02,
          stops: [0, 90, 100],
        },
      },
      markers: {
        size: 5,
        strokeWidth: 2,
        fillOpacity: 1,
        hover: { size: 7 },
      },
      grid: {
        borderColor: "#e0e0f0",
        strokeDashArray: 4,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: true } },
      },
      colors: ["#34d399"],
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "center",
        markers: { radius: 12 },
        fontSize: "13px",
      },
      yaxis: {
        labels: { formatter: (val) => val.toFixed(0) },
      },
      xaxis: {
        categories: [],
        labels: {
          style: { fontSize: "12px", colors: "#888" },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        title: {
          text: "Date",
          style: { fontSize: "12px", color: "#888" },
        },
      },
      tooltip: {
        y: { formatter: (val) => val.toFixed(0) },
      },
      dataLabels: { enabled: false },
    },
  });

  useEffect(() => {
    setMatchGraphLoader(true);
    dashboardGraphService
      .matchGraphApi(matchGraphTimeLimit)
      .then((res) => {
        const responseData = res?.data;
        const list = Array.isArray(responseData) ? responseData : responseData?.data || [];

        if (list.length > 0) {
          const series = [
            {
              name: "Total Matches",
              data: list.map((item) => item.totalMatches || 0),
            },
          ];

          const datesFull = list.map((item) => new Date(item.date));

          setMatchGraphData((prev) => ({
            ...prev,
            series,
            options: {
              ...prev.options,
              xaxis: {
                ...prev.options.xaxis,
                categories: limitCategories(list.map((item) => formatDateLabel(item.date))),
              },
              tooltip: {
                x: {
                  formatter: function (value, { dataPointIndex }) {
                    const fullDate = datesFull[dataPointIndex];
                    return fullDate ? fullDate.toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    }) : "";
                  },
                },
                y: { formatter: (val) => val.toFixed(0) },
              },
            },
          }));
        } else {
          setMatchGraphData((prev) => ({
            ...prev,
            series: [{ name: "Total Matches", data: [] }],
            options: {
              ...prev.options,
              xaxis: {
                ...prev.options.xaxis,
                categories: [],
              },
            },
          }));
        }
      })
      .catch((error) => console.log("matchGraphApi error ====> ", error))
      .finally(() => setMatchGraphLoader(false));
  }, [matchGraphTimeLimit]);

  useEffect(() => {
    let isMounted = true;

    dashboardService
      .Counts()
      .then((response) => {
        if (isMounted) {
          setShowDashboardCount(response && response.data ? response.data : {});
        }
      })
      .catch((error) => console.log("error ====> ", error));

    // Handle payment redirect query params
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment_status') === 'success') {
      Swal.fire({
        icon: 'success',
        title: 'Welcome to Premium Pro! 🎉',
        text: 'Your payment was successful and your premium entitlements are now fully active.',
        confirmButtonColor: '#007D7F'
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('payment_status') === 'cancel') {
      Swal.fire({
        icon: 'info',
        title: 'Payment Canceled',
        text: 'Stripe subscription checkout was canceled.',
        confirmButtonColor: '#000'
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const currentUser = getCurrentUser();
    if (currentUser && currentUser.id) {
      axiosInstance({ role: currentUser.activeRole })
        .get(`common/users/${currentUser.id}`)
        .then(res => {
          if (!isMounted) return;
          if (res.success && res.data) {
            setProfile(res.data);
            const storedUser = getUser(ROLE.SUPER_ADMIN);
            if (storedUser && storedUser.id) {
              const updatedUser = { ...storedUser, ...res.data, token: storedUser.token || res.data.token };
              const currentRole = Array.isArray(storedUser.role) ? storedUser.role[0] : storedUser.role;
              const newRole = Array.isArray(res.data.role) ? res.data.role[0] : res.data.role;

              if (newRole === currentRole) {
                updatedUser.role = storedUser.role;
              }

              setLocalKey(ROLE.SUPER_ADMIN, JSON.stringify(updatedUser));
              if (newRole && currentRole && newRole !== currentRole) {
                window.location.reload();
              }
            }
          }
        })
        .catch(err => console.log("Failed to load user profile:", err));
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubscribe = async () => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) return;
      
      Swal.fire({
        title: 'Redirecting to Stripe...',
        text: 'Please wait while we establish a secure checkout connection.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await paymentsService.CreateCheckoutSession(currentUser.activeRole);
      if (response.success && response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Checkout Failed',
          text: response.message || 'Unable to start checkout session.',
          confirmButtonColor: '#000'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Checkout Error',
        text: handleServerValidations(error),
        confirmButtonColor: '#000'
      });
    }
  };

  return (
    <>
      {profile && profile.stripeSubscriptionAllowed && profile.subscription_tier !== 'ANALYTICS_PRO' && (
        <div className="row row-sm mb-4">
            <div className="col-12">
                <div className="card custom-card overflow-hidden text-white" style={{
                    background: 'linear-gradient(135deg, #007D7F 0%, #0f2963 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0, 125, 127, 0.2)'
                }}>
                    <div className="card-body p-4 position-relative">
                        <div style={{
                            position: 'absolute',
                            top: '-50px',
                            right: '-50px',
                            width: '180px',
                            height: '180px',
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.08)',
                            pointerEvents: 'none'
                        }}></div>
                        
                        <div className="row align-items-center">
                            <div className="col-md-9 col-sm-12">
                                <h4 className="font-weight-bold mb-2 text-white">🌟 Unlock Premium mNetrics Analytics!</h4>
                                <p className="mb-0 text-white" style={{ opacity: 0.9, fontSize: '14px' }}>
                                    You are authorized to upgrade! Get access to advanced Heatmaps, conversion chains, historic reporting, PIR weights, and lineup effectiveness analysis.
                                </p>
                            </div>
                            <div className="col-md-3 col-sm-12 text-md-right mt-3 mt-md-0">
                                <button 
                                    className="btn btn-lg font-weight-bold px-4 py-2"
                                    style={{
                                        backgroundColor: '#ffa502',
                                        color: '#0f2963',
                                        border: 'none',
                                        borderRadius: '30px',
                                        boxShadow: '0 4px 15px rgba(255, 165, 2, 0.4)',
                                        cursor: 'pointer'
                                    }}
                                    onClick={handleSubscribe}
                                >
                                    Subscribe Now
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      )}
      <div className="row row-sm animation_fade">
        <div className="col-sm-12 col-lg-12 col-xl-12">
          <div className="row row-sm">
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <Link to={`/admin/user-management/club-admins/list/1`}>
                <div className="card custom-card">
                  <div className="card-body cp">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp ">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-people-fill" viewBox="0 0 16 16">
                          <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">Club</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.clubAdmins || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <Link to={`/admin/user-management/basic-user/list/1`}>
                <div className="card custom-card">
                  <div className="card-body cp">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp ">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-people-fill" viewBox="0 0 16 16">
                          <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">Basic Users</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.nonAffiliatedUser || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <Link to={`/admin/user-management/parent-admins/list/1`}>
                <div className="card custom-card">
                  <div className="card-body cp">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp ">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-people-fill" viewBox="0 0 16 16">
                          <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">Parents</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.parents || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <Link to={`/admin/user-management/players/list/1`}>
                <div className="card custom-card">
                  <div className="card-body cp">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp ">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-people-fill" viewBox="0 0 16 16">
                          <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">Players</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.players || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <Link to={`/admin/user-management/sub-admins/list/1`}>
                <div className="card custom-card">
                  <div className="card-body cp">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp ">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-people-fill" viewBox="0 0 16 16">
                          <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">Sub Admins</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.subAdmins || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
            {/* <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <Link to={`/admin/cms/default-email-template/list/1`}>
                <div className="card custom-card">
                  <div className="card-body cp">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp ">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-person-fill-exclamation" viewBox="0 0 16 16">
                          <path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0m-9 8c0 1 1 1 1 1h5.256A4.5 4.5 0 0 1 8 12.5a4.5 4.5 0 0 1 1.544-3.393Q8.844 9.002 8 9c-5 0-6 3-6 4" />
                          <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-3.5-2a.5.5 0 0 0-.5.5v1.5a.5.5 0 0 0 1 0V11a.5.5 0 0 0-.5-.5m0 4a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">Email Templates</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.totalEmailTemplates || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div> */}
{/* 
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <Link to={`/admin/cms/faq-management/faq/list/1`}>
                <div className="card custom-card">
                  <div className="card-body cp">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp ">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-journal-check" viewBox="0 0 16 16">
                          <path fillRule="evenodd" d="M10.854 6.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 8.793l2.646-2.647a.5.5 0 0 1 .708 0" />
                          <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1H1V2a2 2 0 0 1 2-2" />
                          <path d="M1 5v-.5a.5.5 0 0 1 1 0V5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0V8h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1z" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">FAQs</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.totalFAQs || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div> */}

            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <Link to={`/admin/dashboard`}>
                <div className="card custom-card">
                  <div className="card-body cp">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp ">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-person-raised-hand" viewBox="0 0 16 16">
                          <path d="M6 6.207v9.043a.75.75 0 0 0 1.5 0V10.5a.5.5 0 0 1 1 0v4.75a.75.75 0 0 0 1.5 0v-8.5a.25.25 0 1 1 .5 0v2.5a.75.75 0 0 0 1.5 0V6.5a3 3 0 0 0-3-3H6.236a1 1 0 0 1-.447-.106l-.33-.165A.83.83 0 0 1 5 2.488V.75a.75.75 0 0 0-1.5 0v2.083c0 .715.404 1.37 1.044 1.689L5.5 5c.32.32.5.754.5 1.207" />
                          <path d="M8 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">New Inquiries</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.newInquiries || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <Link to={`/admin/match-management/match/list/1`}>
                <div className="card custom-card">
                  <div className="card-body cp">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp ">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 16 16">
                          <path d="M2 2h1v2a3 3 0 0 0 3 3h1v2H5a2 2 0 0 0-2 2v1h10v-1a2 2 0 0 0-2-2H9V7h1a3 3 0 0 0 3-3V2h1a1 1 0 0 1 1 1v1a4 4 0 0 1-4 4h-1v2h2a3 3 0 0 1 3 3v2H1v-2a3 3 0 0 1 3-3h2V8H5a4 4 0 0 1-4-4V3a1 1 0 0 1 1-1z" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">Total Matches</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.totalMatches || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div> */}
            {/* <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <Link to={`/admin/cms/pages/list/1`}>
                <div className="card custom-card">
                  <div className="card-body cp">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp ">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-journal-x" viewBox="0 0 16 16">
                          <path fillRule="evenodd" d="M6.146 6.146a.5.5 0 0 1 .708 0L8 7.293l1.146-1.147a.5.5 0 1 1 .708.708L8.707 8l1.147 1.146a.5.5 0 0 1-.708.708L8 8.707 6.854 9.854a.5.5 0 0 1-.708-.708L7.293 8 6.146 6.854a.5.5 0 0 1 0-.708" />
                          <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1H1V2a2 2 0 0 1 2-2" />
                          <path d="M1 5v-.5a.5.5 0 0 1 1 0V5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0V8h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1z" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">Total Pages</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.totalPages || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div> */}
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <Link to={`/admin/team-management/team/list/1`}>
                <div className="card custom-card">
                  <div className="card-body cp">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                          <circle cx="12" cy="6" r="2" />
                          <circle cx="5" cy="9" r="2" />
                          <circle cx="19" cy="9" r="2" />
                          <circle cx="8" cy="4" r="1.8" />
                          <circle cx="16" cy="4" r="1.8" />
                          <path d="M12 10c-3 0-6 1.5-6 4v2h12v-2c0-2.5-3-4-6-4z" />
                          <path d="M5 12c-2 0-4 1-4 3v1h6v-1c0-1.5-1-3-2-3z" />
                          <path d="M19 12c2 0 4 1 4 3v1h-6v-1c0-1.5 1-3 2-3z" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">Total Teams</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.totalTeams || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* ===== Charts Section ===== */}
        <div className="col-sm-12 col-lg-12 col-xl-12 admin-placeholder">
          <div className="row row-sm">

            {/* Graph 1 — User Graph Overview */}
            <div className="col-lg-6">
              <div className="card custom-card mg-b-20">
                <div className="card-body">
                  <div className="card-header border-bottom-0 pt-0 pl-0 pr-0 d-flex">
                    <div>
                      <label className="main-content-label mb-4">Users Overview</label>
                    </div>
                    <div className="ml-auto d-flex align-items-center">
                      <SelectPicker
                        cleanable={false}
                        value={userGraphTimeLimit}
                        onChange={(value) => setUserGraphTimeLimit(value)}
                        size="sm"
                        data={chartFilterOptions}
                        className="dashboard-filter-select"
                        searchable={false}
                      />
                    </div>
                  </div>
                  {userGraphLoader ? (
                    <Placeholder.Graph active />
                  ) : (
                    <Chart
                      options={userGraphData.options}
                      series={userGraphData.series}
                      type="area"
                      height={350}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Graph 2 — Match Graph Overview */}
            <div className="col-lg-6">
              <div className="card custom-card mg-b-20">
                <div className="card-body">
                  <div className="card-header border-bottom-0 pt-0 pl-0 pr-0 d-flex">
                    <div>
                      <label className="main-content-label mb-4">Match Overview</label>
                    </div>
                    <div className="ml-auto d-flex align-items-center">
                      <SelectPicker
                        cleanable={false}
                        value={matchGraphTimeLimit}
                        onChange={(value) => setMatchGraphTimeLimit(value)}
                        size="sm"
                        data={chartFilterOptions}
                        className="dashboard-filter-select"
                        searchable={false}
                      />
                    </div>
                  </div>
                  {matchGraphLoader ? (
                    <Placeholder.Graph active />
                  ) : (
                    <Chart
                      options={matchGraphData.options}
                      series={matchGraphData.series}
                      type="area"
                      height={350}
                    />
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;